import React, { useEffect } from 'react'
import { format } from 'date-fns'
import {
    StyledNightlyLatestEditionContainer,
    StyledNightlyLatestEditionLink,
    StyledCardBody,
    StyledTextBox,
    StyledHeading,
    StyledTitle,
    StyledButtonWrapper,
} from './NightlyLatestEdition.styled'
import { EditionV4DTO } from '@west-australian-newspapers/publication-types'
import { breakpoint, breakpointMax } from '../../../__styling'
import NightlySpecificEdition from '../SpecificEdition/NightlySpecificEdition'
import {
    AllEvents,
    createEditionSelectItemEvent,
    Product,
    scaleImage,
} from '@news-mono/web-common'
import { useLocation } from 'react-router'
import { NightlyLatestEditionType } from '../NightlyLatestEdition.routing'
import { EditionsNightlyButtons } from '../SpecialEditionHome/Components/EditionsNightlyButtons'
import { HeaderPrefix } from '../SpecialEdition/SpecialEdition.styled'

export interface NightlyLatestEditionProps {
    edition: EditionV4DTO
    title: string
    type: NightlyLatestEditionType
    onEvent: (event: AllEvents) => void
    lazyloadImages?: boolean
}
const HomepageLatestEdition: React.FC<NightlyLatestEditionProps> = ({
    edition,
    title,
    lazyloadImages,
    onEvent,
    type,
}) => {
    const publicationDate = new Date(edition.publicationDate)
    const mainImage = edition.mainImages && {
        img: scaleImage(edition.mainImages[0].src, 600, false),
        alt: edition.mainImages[0].altText ?? edition.mainImages[0].captionText,
    }
    const isTheNightlyOn = edition.publication === 'the_nightly_on'
    const isTheNightlyTravel = edition.publication === 'the_nightly_travel'

    const location = useLocation()

    useEffect(() => {
        const hasDigitalParam = location.search.includes('digital-editions')

        if (hasDigitalParam) {
            const ele = document.getElementById('digital-editions')
            const eleTop = ele?.getBoundingClientRect().top
            const offset = window.innerWidth > 768 ? 300 : 200
            if (ele && eleTop) {
                window.scrollTo({
                    top: eleTop - offset,
                    behavior: 'smooth',
                })
            }
        }
    }, [location])

    const handleImageClick = () => {
        if (onEvent && edition.publication !== undefined) {
            onEvent(
                createEditionSelectItemEvent({
                    edition,
                    collectionName: 'TheNightlyOn_HomepageLatestEdition',
                    product: Product.TheNightly,
                    itemDetails: {
                        category: 'thenightly_editions_page_latest_editions',
                        linkText: 'Latest Edition Image',
                        linkUrl: edition.editionWebUrl,
                        imageUrl: edition.mainImages?.[0]?.src,
                    },
                }),
            )
        }
    }

    return (
        <StyledNightlyLatestEditionContainer id="digital-editions">
            <StyledHeading isTop as={type === 'article' ? 'h4' : 'h2'}>
                {title}
            </StyledHeading>
            <StyledCardBody>
                <StyledNightlyLatestEditionLink
                    to={edition.editionWebUrl}
                    onClick={handleImageClick}
                >
                    {mainImage && (
                        <picture>
                            <source
                                srcSet={mainImage.img}
                                media={breakpointMax('sm', false)}
                            />
                            <source
                                srcSet={mainImage.img}
                                media={breakpoint('sm', false)}
                            />
                            <img
                                src={mainImage.img}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null
                                }}
                                alt={mainImage.alt}
                                loading={lazyloadImages ? 'lazy' : undefined}
                            />
                        </picture>
                    )}
                </StyledNightlyLatestEditionLink>
                <StyledTextBox>
                    {isTheNightlyOn && (
                        <HeaderPrefix className={'prefix'}>
                            {'By The Nightly'}
                        </HeaderPrefix>
                    )}
                    <StyledHeading as={type === 'article' ? 'h4' : 'h2'}>
                        {title}
                        {isTheNightlyTravel && (
                            <HeaderPrefix className={'prefix'}>
                                {'By The Nightly'}
                            </HeaderPrefix>
                        )}
                    </StyledHeading>
                    <StyledTitle as={type === 'article' ? 'h5' : 'h3'}>
                        <span>Edition&nbsp;</span>
                        <span>
                            Edition {format(publicationDate, 'd')}
                            &nbsp;
                        </span>
                        <span>{format(publicationDate, 'MMMM yyyy')}</span>
                        <span>{format(publicationDate, 'd MMMM yyyy')}</span>
                    </StyledTitle>
                    <p>{edition.teaser}</p>
                    <StyledButtonWrapper>
                        <EditionsNightlyButtons
                            edition={edition}
                            heading={title}
                            onEvent={onEvent}
                        />
                    </StyledButtonWrapper>
                </StyledTextBox>
            </StyledCardBody>
            <StyledButtonWrapper isBottom>
                <EditionsNightlyButtons
                    edition={edition}
                    heading={title}
                    onEvent={onEvent}
                />
            </StyledButtonWrapper>
        </StyledNightlyLatestEditionContainer>
    )
}

export const NightlyLatestEdition = ({
    type,
    edition,
    onEvent,
    title,
    lazyloadImages,
}: NightlyLatestEditionProps) => {
    switch (type) {
        case 'editions':
            return (
                <NightlySpecificEdition
                    edition={edition}
                    shouldLinkToEdition
                    lazyloadImages={lazyloadImages}
                    onEvent={onEvent}
                />
            )
        case 'homepage':
        case 'article':
            return (
                <HomepageLatestEdition
                    edition={edition}
                    title={title}
                    lazyloadImages={lazyloadImages}
                    onEvent={onEvent}
                    type={type}
                />
            )
        default: {
            const _never: never = type
            return null
        }
    }
}
